import {
  Zap, LineChart, Cpu, Briefcase, ClipboardCheck, Rocket, ArrowUpCircle,
  Cloud, Wrench, BrainCircuit, Fuel, Ship, Radio, Layers,
  Pickaxe, Wheat, Sun, Repeat, ShieldCheck, CalendarClock, Truck, Receipt,
  BarChart3, CreditCard, FileCheck2, Compass, Sparkles, Database,
  Users, PieChart, Building2, Presentation, Lightbulb, GraduationCap,
} from "lucide-react";

/* -----------------------------------------------------------
   SERVICES — implementation-led practice
   ----------------------------------------------------------- */
const APPROACH_TEMPLATE = [
  { title: "Discover", desc: "A structured diagnostic of your current platform, process and people — benchmarked against leading practice in your industry." },
  { title: "Design", desc: "A blueprint calibrated to your operating model, regulatory context and commercial priorities — never a template." },
  { title: "Deliver", desc: "Senior practitioners embedded with your teams, working transparently against a jointly-owned delivery roadmap." },
  { title: "Sustain", desc: "Continuous improvement, knowledge transfer and outcome-based support after go-live." },
];

export const SERVICES = [
  {
    slug: "etrm", title: "ETRM Advisory & Delivery", icon: Zap,
    short: "Strategy, blueprint and end-to-end delivery across the ETRM value chain.",
    overview: "EnPrex partners with energy trading organizations to design, implement and evolve enterprise ETRM capability. Our engagements combine deep functional expertise with sharp commercial judgement, ensuring the platform reflects how your business actually trades — not how a vendor manual describes trading.",
    challenges: [
      "Legacy ETRM platforms unable to support new products, markets or trading strategies",
      "Fragmented risk views across desks, entities and asset classes",
      "Manual workflows introducing operational risk and delaying month-end",
      "Data quality issues undermining regulatory reporting and P&L integrity",
      "Rising cost-to-serve with declining trader productivity",
    ],
    outcomes: [
      "Real-time, cross-commodity exposure and P&L visibility",
      "Faster onboarding of new products, markets and counterparties",
      "Reduction in manual controls and month-end cycle time",
      "Auditable regulatory reporting across EMIR, REMIT, Dodd-Frank",
      "Lower total cost of ownership and better trader experience",
    ],
    deliverables: [
      "ETRM target operating model & platform strategy",
      "Vendor evaluation & selection artefacts",
      "Functional & non-functional requirements",
      "Reference architecture and integration blueprint",
      "Delivery roadmap with prioritised value releases",
    ],
    value: "Clients typically achieve materially improved exposure visibility, faster onboarding of new products and markets, and a durable reduction in manual controls across the trading enterprise.",
  },
  {
    slug: "ctrm", title: "CTRM Advisory & Delivery", icon: LineChart,
    short: "Multi-commodity CTRM transformation for metals, ags, softs and refined products.",
    overview: "Our CTRM practice supports trading houses, producers and end-users who need a platform that respects the physical realities of their commodities — quality specifications, weight adjustments, logistics, warehousing and provisional pricing.",
    challenges: [
      "Physical complexity poorly modelled in generic financial platforms",
      "Provisional pricing, quality adjustments and quotational-period mechanics driving manual work",
      "Fragmented logistics, position and P&L views",
      "Difficulty scaling to new commodities without heavy customization",
      "Regulatory obligations across multiple jurisdictions and product classes",
    ],
    outcomes: [
      "Accurate, near-real-time position and exposure across physical & financial",
      "Automated provisional pricing and quality-based final pricing",
      "Integrated logistics, inventory and settlement workflows",
      "Faster time-to-market for new commodities and structured trades",
      "Confident, auditable regulatory reporting",
    ],
    deliverables: [
      "CTRM target state & platform strategy",
      "Commodity coverage & product roadmap",
      "Physical logistics & inventory reference design",
      "Integration architecture with market data, ERP and treasury",
      "Migration approach with data quality remediation plan",
    ],
    value: "Clients materially compress physical trade cycle-time, reduce spreadsheet dependency across middle and back office, and unlock the ability to enter adjacent commodities without a platform re-architecture.",
  },
  {
    slug: "endur-consulting", title: "Openlink Endur Consulting", icon: Cpu,
    short: "Independent advisory across strategy, roadmap, module scoping and platform optimisation.",
    overview: "EnPrex is a specialist advisor to organizations running — or considering — Openlink Endur. We bring deep Endur expertise across trading, risk, operations and settlements, combined with true independence from vendor influence.",
    challenges: [
      "Endur estate that has drifted from its original design intent",
      "Uncertainty around the right upgrade path or cloud migration",
      "Performance and scalability challenges under growing trade volumes",
      "Skills gaps between business change and platform configuration",
      "High run-cost and unclear alignment to business outcomes",
    ],
    outcomes: [
      "Clear, prioritised Endur roadmap aligned to business strategy",
      "Rationalised platform footprint and reduced technical debt",
      "Improved trader and operations experience on Endur",
      "Confident upgrade or cloud migration decisions",
      "Measurable reduction in Endur run-cost per trade",
    ],
    deliverables: [
      "Endur estate assessment & maturity benchmark",
      "Endur target-state architecture & module roadmap",
      "Upgrade / migration decision paper for executive sponsors",
      "Optimisation backlog with business case per initiative",
      "Endur operating model & capability plan",
    ],
    value: "Clients typically identify significant avoidable Endur run-cost, a clearer upgrade path, and a business-endorsed Endur roadmap senior leadership can defend.",
  },
  {
    slug: "endur-implementation", title: "Openlink Endur Implementation", icon: Rocket,
    short: "End-to-end Endur implementations with reference architectures and accelerators.",
    overview: "We deliver end-to-end Endur implementations for enterprise clients — combining our reference architecture, delivery playbook, functional accelerators and senior practitioner-led delivery model.",
    challenges: [
      "Multi-year, high-stakes program with executive visibility",
      "Complex cross-commodity, cross-entity scope",
      "Data migration from legacy platforms carrying quality debt",
      "Change management across trading, risk, operations and finance",
      "Balancing platform standardisation against business specificity",
    ],
    outcomes: [
      "Predictable, milestone-based delivery on Endur",
      "Cutover confidence backed by rehearsed parallel runs",
      "Business teams fluent on the new platform from day one",
      "Reduced customization footprint versus legacy",
      "A platform ready to scale to new products and markets",
    ],
    deliverables: [
      "End-to-end program governance & PMO",
      "Functional & delivery streams led by senior practitioners",
      "Data migration approach with quality remediation",
      "Cutover, hyper-care and business-readiness plans",
      "Post-implementation optimisation backlog",
    ],
    value: "EnPrex implementations are designed to hit go-live safely and leave your team with the operating rhythm and capability to keep improving the platform long after we hand over.",
  },
  {
    slug: "endur-upgrade", title: "Endur Upgrade & Migration", icon: ArrowUpCircle,
    short: "Zero-surprise version upgrades and legacy-to-cloud migrations backed by automated regression.",
    overview: "Endur upgrades and migrations are inflection points. Done well, they reset technical debt and unlock new capability. Done badly, they consume years and reputation. EnPrex brings a disciplined, artefact-driven approach that removes surprise from the cutover.",
    challenges: [
      "Heavy customization that resists straight upgrade paths",
      "Automated regression coverage below acceptable thresholds",
      "Unclear data and reference migration approach",
      "Business appetite for change fatigued by earlier programs",
      "Cutover windows constrained by netting, settlement and reporting cycles",
    ],
    outcomes: [
      "Business-endorsed upgrade / migration decision",
      "Predictable cutover with rehearsed rollback",
      "Materially reduced customization post-upgrade",
      "Improved platform performance and supportability",
      "Team fluent on the new version from day one",
    ],
    deliverables: [
      "Upgrade / migration decision paper",
      "Automated regression pack across the trade lifecycle",
      "Data & configuration migration approach",
      "Cutover rehearsal & rollback plan",
      "Hyper-care operating model",
    ],
    value: "Clients complete Endur upgrades with fewer defects, shorter parallel runs and a lower go-forward customization footprint — turning a defensive program into a platform reset.",
  },
  {
    slug: "system-integration", title: "System Integration", icon: Layers,
    short: "Integrate ETRM with market data, risk, treasury, ERP and downstream reporting.",
    overview: "We design and deliver the integration surfaces that connect your trading platform to the wider enterprise — market data providers, treasury and cash, general ledger, credit systems, regulatory reporting and analytics.",
    challenges: [
      "Point-to-point integrations accumulating over years",
      "Fragile interfaces that fail with every platform upgrade",
      "Reconciliation workload masking systemic data quality issues",
      "Regulatory feeds under-tested against real-world edge cases",
      "Unclear ownership between platform, integration and consumer teams",
    ],
    outcomes: [
      "A standardised, documented integration surface",
      "Upgrade-safe interfaces with clear contracts",
      "Reduced reconciliation and exception handling",
      "Faster onboarding of new counterparties, products and reports",
      "Clear ownership across platform and consumer teams",
    ],
    deliverables: [
      "Integration reference architecture & patterns",
      "API and messaging design standards",
      "Interface catalogue & ownership model",
      "Test packs for critical trade lifecycle interfaces",
      "Migration plan from legacy point-to-point to canonical model",
    ],
    value: "A rationalised integration estate materially reduces cost of change per business initiative and eliminates the fragility that too often surrounds critical trading platforms.",
  },
  {
    slug: "business-analysis", title: "Business Analysis", icon: Briefcase,
    short: "Senior domain-fluent BAs — requirements, process mapping and functional design.",
    overview: "The EnPrex BA practice is built on a simple conviction — business analysis in trading is a craft. Our BAs pair deep trading domain knowledge with the discipline to produce specifications that developers can implement without ambiguity.",
    challenges: [
      "Requirements that fail to survive contact with the trading floor",
      "Process maps that describe theory rather than reality",
      "Functional specifications missing edge cases and lifecycle events",
      "Weak traceability from business need to test evidence",
      "BAs without the domain fluency to challenge or refine requirements",
    ],
    outcomes: [
      "Requirements that trading, risk and operations sign with confidence",
      "Fewer downstream design and build rework cycles",
      "Complete traceability from need through delivery",
      "Faster and safer functional change",
      "A stronger internal BA capability post-engagement",
    ],
    deliverables: [
      "Business & functional requirements documentation",
      "Process maps (current & target state)",
      "Use case & scenario libraries",
      "Requirements traceability matrix",
      "BA templates, standards and coaching material",
    ],
    value: "Domain-fluent BAs are the single biggest de-risking lever on any ETRM program. Clients compress timelines, reduce build rework and produce systems that traders actually use.",
  },
  {
    slug: "project-management", title: "Program & Project Management", icon: ClipboardCheck,
    short: "PMO, governance and delivery leadership for multi-year trading technology programs.",
    overview: "EnPrex provides delivery leadership for the most demanding trading technology programs — those that span multiple years, multiple continents and multiple executive sponsors.",
    challenges: [
      "Programs stalled by governance drift or unclear accountability",
      "Weak visibility of risk, dependency and value delivered",
      "Vendor and supplier ecosystems difficult to align",
      "Change fatigue eroding executive sponsorship",
      "PMO metrics that report activity, not outcomes",
    ],
    outcomes: [
      "Clear program governance and accountability",
      "Executive-ready reporting on risk, cost and value",
      "Vendors and suppliers aligned to the same milestones",
      "Predictable delivery on business-critical outcomes",
      "A program the executive sponsor is proud to defend",
    ],
    deliverables: [
      "PMO operating model & tooling",
      "Governance charters & steering material",
      "Delivery roadmap with dependency management",
      "Executive & board reporting cadence",
      "Value tracking & benefits realisation framework",
    ],
    value: "Our PMOs are outcome-anchored — every artefact ties back to a business benefit an executive can defend. Programs regain momentum, and boards regain confidence.",
  },
  {
    slug: "solution-architecture", title: "Solution Architecture", icon: Compass,
    short: "Reference architectures and platform blueprints for the modern trading estate.",
    overview: "Our solution architects design trading estates built to scale — with clean integration surfaces, principled reference data and reference architectures robust to vendor and market change.",
    challenges: [
      "Point-to-point integrations accumulating over years",
      "Reference data governance underdeveloped for the trading domain",
      "Cloud and on-premise fragments coexisting without a target picture",
      "Architecture decisions made in isolation from business intent",
      "Rising cost of change per business initiative",
    ],
    outcomes: [
      "A published, business-endorsed target architecture",
      "Standardised integration and messaging patterns",
      "Rationalised platform footprint",
      "Improved change velocity and predictability",
      "A stronger internal architecture capability",
    ],
    deliverables: [
      "Current & target architecture artefacts",
      "Integration reference patterns and playbooks",
      "Reference data governance model",
      "Architecture decision records",
      "Roadmap of prioritised architecture initiatives",
    ],
    value: "Clients gain a defensible architecture story, lower cost of change per initiative, and a technology estate that scales with commercial ambition — not against it.",
  },
  {
    slug: "digital-transformation", title: "Digital Transformation", icon: Sparkles,
    short: "Operating-model reinvention across front, middle and back office.",
    overview: "EnPrex digital transformation programs go beyond platform refresh. We help clients rewire the operating model — trading, risk, operations, finance and technology — around modern data, cloud and AI.",
    challenges: [
      "Digital investment fragmented across silos",
      "Legacy operating model constraining new commercial ambition",
      "Change fatigue from previous, incomplete transformations",
      "Data foundations too weak to underpin AI or advanced analytics",
      "Leadership and workforce readiness lagging technology change",
    ],
    outcomes: [
      "A coherent, executive-owned digital agenda",
      "Modern data foundations feeding trading, risk and finance",
      "Faster, safer decision cycles across the enterprise",
      "Measurable improvements in productivity and cost-to-serve",
      "A workforce genuinely uplifted for the new operating model",
    ],
    deliverables: [
      "Digital vision & operating-model target state",
      "Prioritised transformation portfolio",
      "Data & AI foundations blueprint",
      "Change and communication framework",
      "Executive dashboard for portfolio value tracking",
    ],
    value: "Our transformations are anchored in commercial outcomes and sequenced to survive leadership cycles — turning ambition into durable capability.",
  },
  {
    slug: "cloud-modernization", title: "Cloud Modernization", icon: Cloud,
    short: "Move the trading estate to Azure or AWS with the deterministic performance the desk needs.",
    overview: "EnPrex helps trading organizations move to cloud on terms that respect the performance, security and regulatory obligations that trading platforms demand.",
    challenges: [
      "Uncertainty on the right migration pattern per workload",
      "Regulatory, data residency and control obligations",
      "Cost surprises from unbounded consumption",
      "Skills gaps between traditional operations and cloud native",
      "Legacy applications resistant to straight lift-and-shift",
    ],
    outcomes: [
      "A cloud strategy grounded in commercial and regulatory reality",
      "A workload-by-workload migration decision framework",
      "Predictable cloud economics with strong FinOps discipline",
      "Modern operations, observability and security posture",
      "A team confident and capable in cloud native delivery",
    ],
    deliverables: [
      "Cloud strategy & landing zone reference",
      "Workload migration decision framework",
      "FinOps operating model",
      "Cloud security & compliance blueprint",
      "Skills and capability development plan",
    ],
    value: "Trading estates on EnPrex-designed cloud foundations are faster, cheaper to change, and materially better positioned to adopt AI and advanced analytics.",
  },
  {
    slug: "data-analytics", title: "Data & Analytics", icon: BarChart3,
    short: "From data lake to decision layer — trader cockpits, exposure analytics and real-time P&L.",
    overview: "Our data & analytics practice builds decision layers — not dashboards. We help trading organizations turn their data into signals that traders, risk managers and executives actually act on.",
    challenges: [
      "Dashboards that describe the past but do not inform the next decision",
      "Data quality gaps eroding trust in trading and risk numbers",
      "Fragmented reporting across trading, risk, operations and finance",
      "Slow, batch-oriented analytics unable to keep up with intraday markets",
      "Analytics teams isolated from the trading floor",
    ],
    outcomes: [
      "A trusted, real-time trading decision layer",
      "Consolidated reporting across desks and functions",
      "Faster identification of P&L and exposure anomalies",
      "Analytics adopted by the desk, not admired from a distance",
      "A modern data platform ready for AI",
    ],
    deliverables: [
      "Data & analytics target architecture",
      "Trader cockpit and executive dashboard designs",
      "Data quality remediation plan",
      "Streaming and real-time reference implementation",
      "Data operating model & governance",
    ],
    value: "EnPrex analytics engagements are measured in P&L discovered, decisions accelerated and risk exposure surfaced — not in dashboards produced.",
  },
  {
    slug: "ai-energy-trading", title: "AI for Energy Trading", icon: BrainCircuit,
    short: "Industrialise the AI use cases that actually move P&L and reduce risk.",
    overview: "EnPrex helps clients cut through AI hype and industrialise the use cases that meaningfully move P&L, reduce risk or free up trader time — with the governance a regulated trading environment demands.",
    challenges: [
      "Executive pressure to adopt AI without a clear use-case portfolio",
      "Data foundations not yet ready to support production ML or LLMs",
      "Model governance and explainability gaps in a regulated context",
      "Risk of shadow AI adoption on the trading floor",
      "Difficulty scaling promising proofs-of-concept to production",
    ],
    outcomes: [
      "A prioritised, business-endorsed AI portfolio",
      "Production-ready ML and generative-AI use cases with measurable value",
      "Robust model governance and observability",
      "Traders using AI copilots as an everyday tool",
      "Executive confidence in AI risk posture",
    ],
    deliverables: [
      "AI strategy & use-case portfolio",
      "AI reference architecture (MLOps + GenAI)",
      "Model governance & risk framework",
      "Production AI use cases (typically three to five in wave one)",
      "AI operating model & capability plan",
    ],
    value: "Our AI programs are structured to industrialise — moving from experimentation to production use cases the desk relies on daily, with governance the board can defend.",
  },
  {
    slug: "managed-services", title: "Managed Services", icon: ShieldCheck,
    short: "Outcome-priced managed services for Endur and adjacent trading platforms.",
    overview: "Our managed services are priced against business outcomes — platform uptime, mean-time-to-recover, value delivered per quarter — not against headcount and hours. This alignment fundamentally changes how the partnership behaves.",
    challenges: [
      "Existing support arrangements priced on activity, not value",
      "Improvement backlog perpetually deprioritised",
      "Knowledge concentration in individuals rather than documentation",
      "Weak reporting cadence to platform sponsors",
      "Difficulty flexing capacity to match demand",
    ],
    outcomes: [
      "SLA-backed uptime and MTTR aligned to trading criticality",
      "Continuous platform improvement, not just break-fix",
      "Documented knowledge and reduced key-person risk",
      "Transparent quarterly value stories to sponsors",
      "Predictable cost with flexible capacity",
    ],
    deliverables: [
      "Managed services operating model & SLA framework",
      "Transition plan from incumbent arrangement",
      "Continuous improvement backlog & governance",
      "Quarterly business review cadence",
      "Value reporting & benefits realisation",
    ],
    value: "Clients move from a support arrangement that costs a lot and improves little, to a managed services partnership that keeps the platform ahead of the business.",
  },
  {
    slug: "process-optimization", title: "Trading Process Optimization", icon: Repeat,
    short: "Lean review of the front-to-back workflow — cycle time, control and margin improvements.",
    overview: "EnPrex process optimisation looks at your trading workflow through both a lean and a commercial lens — where cycle time is being lost, where controls are duplicated, where margin is silently leaking.",
    challenges: [
      "Manual controls duplicated across trading, risk and operations",
      "Cycle time between trade capture and confirmation drifting upward",
      "Reconciliation workload masking systemic data quality issues",
      "Settlement disputes eroding counterparty relationships",
      "Process improvement projects that stall at the pilot stage",
    ],
    outcomes: [
      "Shorter cycle times across the front-to-back workflow",
      "Reduced manual controls and reconciliation burden",
      "Fewer settlement disputes and faster resolution",
      "Clearer ownership and accountability across teams",
      "A continuous improvement culture that survives us",
    ],
    deliverables: [
      "End-to-end process maps and pain-point analysis",
      "Prioritised optimisation backlog with business cases",
      "Automation and tooling recommendations",
      "Control rationalisation plan",
      "Ownership and accountability framework",
    ],
    value: "Our process work quietly pays for itself — through recovered trader time, reduced operations effort, fewer disputes and faster close cycles.",
  },
  {
    slug: "technology-advisory", title: "Technology Advisory", icon: Lightbulb,
    short: "Independent counsel to boards, CIOs and executive sponsors on trading technology decisions.",
    overview: "EnPrex acts as an independent technology advisor to boards, CIOs and executive sponsors — bringing the deep trading-domain fluency required to challenge vendors, calibrate roadmaps and assure critical programs.",
    challenges: [
      "Vendor selections driven by demo appeal rather than fit",
      "Roadmaps under-tested against commercial and regulatory pressure",
      "Programs in flight with unclear delivery health",
      "Boards asking harder questions than internal teams can answer",
      "Difficulty accessing independent, domain-fluent advice",
    ],
    outcomes: [
      "Defensible technology and vendor decisions",
      "Boards and sponsors with clear line-of-sight to program health",
      "Programs recalibrated before they run into trouble",
      "Stronger negotiating position with strategic vendors",
      "A leadership team confident in the technology agenda",
    ],
    deliverables: [
      "Vendor evaluation framework & selection report",
      "Independent program health assessments",
      "Board & executive briefings",
      "Technology strategy papers",
      "Vendor & contract negotiation support",
    ],
    value: "Boards and CIOs use EnPrex as the specialist adviser they call when the decisions matter most — sharp, independent, and grounded in the trading domain.",
  },
];

SERVICES.forEach((s) => { s.approach = APPROACH_TEMPLATE; });

/* -----------------------------------------------------------
   INDUSTRIES / SOLUTIONS / TECHNOLOGIES
   ----------------------------------------------------------- */
export const INDUSTRIES = [
  { slug: "oil-gas", name: "Oil & Gas", icon: Fuel, image: "https://images.unsplash.com/photo-1727461553668-45322401f863?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Upstream, midstream and downstream trading — crude, refined products, NGLs and derivatives — supported by decades of practitioner expertise." },
  { slug: "lng", name: "LNG", icon: Ship, image: "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Long-term contracts, spot cargoes, JKM-linked flows and destination flexibility — modelled with the sophistication the modern LNG market demands." },
  { slug: "power", name: "Power", icon: Zap, image: "https://images.unsplash.com/photo-1548337138-e87d889cc369?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Day-ahead, intraday, capacity and ancillary services trading for deregulated markets with the low-latency data foundations these markets require." },
  { slug: "utilities", name: "Utilities", icon: Radio, image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Integrated utility trading, hedging and regulatory compliance across power, gas, emissions and increasingly renewables." },
  { slug: "metals", name: "Metals", icon: Layers, image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Base and precious metals — LME workflows, warehousing, hedging and physical settlement modelled to enterprise standard." },
  { slug: "mining", name: "Mining", icon: Pickaxe, image: "https://images.unsplash.com/photo-1523848309072-c199db53f137?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Concentrate-to-cathode value chain, offtake agreements, quotational-period pricing and provisional invoicing." },
  { slug: "agriculture", name: "Agriculture", icon: Wheat, image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "Grains, oilseeds, softs and dairy — origination, logistics, quality management and hedging across global supply chains." },
  { slug: "renewable-energy", name: "Renewable Energy", icon: Sun, image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", desc: "PPAs, RECs, guarantees of origin and battery storage optimisation for the energy transition — modelled alongside conventional trading." },
];

export const SOLUTIONS = [
  { title: "Trade Lifecycle", icon: Repeat, desc: "Front-to-back capture, confirmation, allocation and lifecycle events across physical and financial trades." },
  { title: "Risk Management", icon: ShieldCheck, desc: "Position, P&L, VaR and stress testing across cross-commodity portfolios in real time." },
  { title: "Scheduling", icon: CalendarClock, desc: "Nomination, gas balancing, power scheduling and physical fulfilment workflows integrated with the lifecycle." },
  { title: "Logistics", icon: Truck, desc: "Multi-modal logistics — vessels, pipelines, rail — with demurrage, inventory and inspection optimisation." },
  { title: "Settlements", icon: Receipt, desc: "Automated invoicing, netting, dispute management and cash allocation across a global counterparty network." },
  { title: "Market Risk", icon: BarChart3, desc: "Sensitivities, VaR, scenario analytics and greeks for options and structured trades, with model governance." },
  { title: "Credit Risk", icon: CreditCard, desc: "PFE, exposure, limit monitoring, collateral and margin management for trading counterparties." },
  { title: "Regulatory Reporting", icon: FileCheck2, desc: "EMIR, REMIT, Dodd-Frank, MiFID II — full trade repository connectivity, reconciliation and audit trail." },
  { title: "Data Analytics", icon: Database, desc: "Trader cockpits, exposure analytics and real-time P&L on modern data platforms." },
  { title: "AI Solutions", icon: BrainCircuit, desc: "Predictive P&L, ML-driven signals and generative-AI copilots industrialised across the trading floor." },
];

export const TECHNOLOGIES = [
  { name: "Openlink Endur", category: "ETRM/CTRM Platform", desc: "Best-in-class multi-commodity trading and risk platform with deep configuration capability." },
  { name: "Allegro", category: "ETRM/CTRM Platform", desc: "Energy trading platform used across midstream, power and gas trading operations globally." },
  { name: "RightAngle", category: "ETRM/CTRM Platform", desc: "Physical oil, refined products and NGL trading & scheduling platform." },
  { name: "AspectCTRM", category: "ETRM/CTRM Platform", desc: "Cloud-native multi-commodity CTRM for metals, softs and physical energy trading." },
  { name: "SAP", category: "Enterprise Systems", desc: "S/4HANA, Commodity Management and Treasury integration for the trading enterprise." },
  { name: "Power BI", category: "Analytics", desc: "Executive dashboards, trader cockpits and regulatory canvases at enterprise scale." },
  { name: "Snowflake", category: "Data Platform", desc: "Enterprise data cloud powering trading analytics and decision-layer accelerators." },
  { name: "Azure", category: "Cloud Platform", desc: "Preferred cloud for regulated European trading operations with full landing-zone accelerators." },
  { name: "AWS", category: "Cloud Platform", desc: "Trading-grade AWS architectures with low-latency market data ingestion." },
  { name: "Databricks", category: "Data Platform", desc: "Lakehouse architectures for trading analytics, ML pipelines and real-time streaming." },
];

/* -----------------------------------------------------------
   TRAINING — corporate, functional, executive only.
   Small, premium, B2B — not an academy.
   ----------------------------------------------------------- */
export const TRAINING_PROGRAMS = [
  {
    slug: "corporate-endur-foundations",
    title: "Corporate Endur Foundations",
    icon: Cpu,
    format: "On-site or virtual",
    audience: "Enterprise business teams",
    short: "A functional grounding in Openlink Endur for business, risk and operations staff.",
    overview: "A structured functional curriculum introducing Openlink Endur to enterprise business teams — how Endur models the trade lifecycle, how trading, risk, operations and settlements are supported, and how business change is delivered through the platform. Content is functional and business-oriented, calibrated to your platform version and module scope.",
    modules: [
      "Endur Platform Foundations — architecture and building blocks",
      "The Trade Lifecycle in Endur — capture, confirmation, lifecycle events",
      "Risk, P&L and Exposure Analytics",
      "Operations, Scheduling and Settlements",
      "Reference Data, Configuration and Governance",
      "Business Change on the Endur Platform",
    ],
    outcomes: [
      "Confident, day-one-productive business users on Endur",
      "Faster onboarding of new joiners across trading, risk and operations",
      "Reduced dependency on scarce specialist support",
      "A common vocabulary between business and delivery teams",
    ],
    duration: "Two to four days — modular",
  },
  {
    slug: "corporate-etrm-foundations",
    title: "Corporate ETRM Foundations",
    icon: Zap,
    format: "On-site or virtual",
    audience: "Cross-functional enterprise teams",
    short: "A shared foundation in Energy Trading & Risk Management across trading, risk, operations and finance.",
    overview: "A cross-functional Energy Trading & Risk Management foundation program for organizations onboarding new staff, reinforcing common vocabulary, or preparing teams for an ETRM change program.",
    modules: [
      "Energy Markets Foundations — power, gas, LNG, oil",
      "The Trade Lifecycle — front-to-back",
      "Risk Management — market, credit, operational",
      "Operations, Scheduling and Settlements",
      "Regulatory Landscape — EMIR, REMIT, Dodd-Frank",
      "The ETRM Platform Landscape",
    ],
    outcomes: [
      "Shared vocabulary and mental model across the trading enterprise",
      "Sharper business-technology conversations on ETRM initiatives",
      "Faster onboarding of new joiners across functions",
      "A stronger foundation for downstream ETRM change programs",
    ],
    duration: "Two to three days",
  },
  {
    slug: "corporate-ctrm-foundations",
    title: "Corporate CTRM Foundations",
    icon: LineChart,
    format: "On-site or virtual",
    audience: "Cross-functional enterprise teams",
    short: "A shared foundation in Commodity Trading & Risk Management for physical and financial teams.",
    overview: "A dedicated CTRM curriculum for organizations trading metals, softs, agricultural products and refined commodities — with the physical, quality and logistics complexity these markets require.",
    modules: [
      "Commodity Markets Foundations",
      "Physical Trading — Quality, Logistics, Warehousing",
      "Provisional Pricing and Quotational Periods",
      "Risk Management for Physical Commodities",
      "Settlements, Invoicing and Disputes",
      "The CTRM Platform Landscape",
    ],
    outcomes: [
      "Cross-functional fluency in the physical trading lifecycle",
      "Sharper conversations between trading, operations and finance",
      "Better preparation for CTRM change programs",
      "Improved onboarding of new joiners",
    ],
    duration: "Two to three days",
  },
  {
    slug: "executive-briefings",
    title: "Executive Briefings & Workshops",
    icon: Presentation,
    format: "Half-day or full-day",
    audience: "Leadership teams and boards",
    short: "Curated executive briefings on the trends reshaping trading technology.",
    overview: "Curated executive briefings and workshops for leadership teams and boards on the trends reshaping trading technology — AI, cloud, energy transition, regulatory change — designed to sharpen the decisions that shape multi-year investment.",
    modules: [
      "The State of ETRM and CTRM — 2026 and beyond",
      "AI in Trading — What Actually Moves P&L",
      "Cloud for Trading Platforms — Enterprise Reality",
      "Energy Transition and Trading Technology",
      "Regulatory Change — What to Prepare For",
    ],
    outcomes: [
      "Executive teams with a sharpened point of view",
      "Better-informed technology investment decisions",
      "Alignment across board, C-suite and platform sponsors",
    ],
    duration: "Half-day or full-day session",
  },
];

/* -----------------------------------------------------------
   Reusables
   ----------------------------------------------------------- */
export const STATS = [
  { value: "20+", label: "Trading Technologies" },
  { value: "10+", label: "Client Industries" },
  { value: "500+", label: "Consulting Days Delivered" },
  { value: "24/7", label: "Managed Services Coverage" },
];

export const INQUIRY_TYPES = [
  "Book a Consultation",
  "Request a Proposal",
  "Request an Endur Assessment",
  "Request an Implementation Partner",
  "Request Managed Services",
  "Request Corporate Training",
  "Partnership & Alliances",
  "Other",
];

export const HERO_PROMISES = [
  { title: "Global Perspective", desc: "Across every major energy & commodity market." },
  { title: "Deep Expertise", desc: "ETRM · CTRM · Endur — practitioner-led." },
  { title: "Proven Approach", desc: "Methodical. Measurable. Reliable." },
  { title: "Sustainable Impact", desc: "Value that endures beyond go-live." },
];

export const IMPLEMENTATION_REASONS = [
  {
    title: "Practitioner-led delivery",
    desc: "Every engagement is fronted by senior consultants who have delivered comparable implementations at the world's largest trading organizations. No juniors on the critical path.",
  },
  {
    title: "Reference architectures & accelerators",
    desc: "We do not start from a blank page. Our reference architectures, functional accelerators and pre-built regression packs shorten timelines by months.",
  },
  {
    title: "Independent, vendor-neutral counsel",
    desc: "We are not incentivised by any vendor. Our advice is calibrated to your reality alone — commercial, regulatory and operational.",
  },
  {
    title: "Outcome-based accountability",
    desc: "We commit to business outcomes — trading-day availability, cycle-time compression, value realised — not to headcount and hours.",
  },
  {
    title: "Global delivery, local nuance",
    desc: "We deliver across North America, Europe, MENA and APAC with senior consultants who understand each region's regulatory and market context.",
  },
  {
    title: "Long-term partnership mindset",
    desc: "We optimise for the relationship, not the transaction. Managed services, continuous improvement and knowledge transfer follow every implementation.",
  },
];
